import { useState } from "react";
import { Ava } from "../atoms/Ava.jsx";
import { Logo } from "../atoms/Logo.jsx";

/**
 * Persona Owner layout — limited sidebar for persona owners.
 * Shows only their single persona with navigation to allowed sub-pages.
 * No persona switcher, no admin-only menu items (Applications, Rewards, New Persona).
 */

const PO_NAV_ITEMS = [
  { id: "po-dashboard", icon: "🏠", label: "Dashboard", url: "/owner/dashboard" },
];

export function PoLayout({
  view,
  persona,
  onSetView,
  onLogout,
  children,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const pid = persona?.id;

  const personaNav = persona
    ? [
        { id: "analytics", icon: "📊", label: "Analytics",      url: `/admin/personas/${pid}/analytics` },
        { id: "kb",        icon: "📚", label: "Knowledge Base", url: `/admin/personas/${pid}/kb` },
        { id: "chat",      icon: "🗣️", label: "Preview Chat",   url: `/admin/personas/${pid}/chat` },
        { id: "profile",   icon: "📋", label: "Profile",        url: `/admin/personas/${pid}` },
        { id: "statement", icon: "📝", label: "Statement",      url: `/admin/personas/${pid}/statement` },
        { id: "tone",      icon: "🎭", label: "Tone",           url: `/admin/personas/${pid}/tone` },
        { id: "cache",     icon: "⚡", label: "Cache",          url: `/admin/personas/${pid}/cache` },
        { id: "credits",   icon: "💰", label: "Credits",        url: `/admin/personas/${pid}/credits` },
      ]
    : [];

  const handleNav = (item) => {
    window.history.pushState({}, "", item.url);
    onSetView(item.id);
  };

  const S = {
    sidebar: {
      width: collapsed ? 56 : 220,
      minWidth: collapsed ? 56 : 220,
      background: "#0a0f1a",
      borderRight: "1px solid #1e2d45",
      display: "flex",
      flexDirection: "column",
      transition: "width 0.2s, min-width 0.2s",
      overflow: "hidden",
      flexShrink: 0,
    },
    header: {
      padding: collapsed ? "12px 8px" : "16px 14px",
      borderBottom: "1px solid #1e2d45",
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    logo: {
      width: 32,
      height: 32,
      background: "linear-gradient(135deg, #10b981, #059669)",
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 16,
      fontWeight: 900,
      color: "#fff",
      flexShrink: 0,
    },
    navItem: (active) => ({
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: collapsed ? "10px 16px" : "10px 14px",
      background: active ? "#064e3b" : "transparent",
      color: active ? "#34d399" : "#8b95a8",
      border: "none",
      borderLeft: active ? "3px solid #10b981" : "3px solid transparent",
      cursor: "pointer",
      fontSize: 13,
      fontFamily: "inherit",
      fontWeight: active ? 600 : 400,
      width: "100%",
      textAlign: "left",
      transition: "background 0.15s",
    }),
    section: {
      fontSize: 9,
      fontWeight: 700,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: "#475569",
      padding: "16px 14px 6px",
    },
  };

  return (
    <div style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
      <div style={S.sidebar}>
        {/* Header */}
        <div style={S.header}>
          <Logo size={28} showText={false} />
          {!collapsed && (
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9" }}>QuestionMe</div>
              <div style={{ fontSize: 9, color: "#10b981", textTransform: "uppercase", letterSpacing: "0.1em" }}>Persona Owner</div>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 14, padding: 4 }}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? "▶" : "◀"}
          </button>
        </div>

        {/* Persona info */}
        {!collapsed && persona && (
          <div style={{ padding: "10px 14px", borderBottom: "1px solid #1e2d45" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "#0f1623", border: "1px solid #1e2d45", borderRadius: 8 }}>
              <Ava name={persona.name} size={24} color={persona.accent_color || persona.accentColor} />
              <div style={{ flex: 1, overflow: "hidden" }}>
                <div style={{ fontWeight: 600, fontSize: 12, color: "#e2e8f0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {persona.name}
                </div>
                <div style={{ fontSize: 10, color: "#64748b" }}>{persona.role}</div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav style={{ flex: 1, overflowY: "auto", paddingTop: 8 }}>
          {!collapsed && <div style={S.section}>Navigation</div>}
          {PO_NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              style={S.navItem(view === item.id)}
              onClick={() => handleNav(item)}
              title={collapsed ? item.label : undefined}
            >
              <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon}</span>
              {!collapsed && <span style={{ flex: 1 }}>{item.label}</span>}
            </button>
          ))}

          {persona && personaNav.length > 0 && (
            <>
              {!collapsed && (
                <div style={{ ...S.section, marginTop: 8 }}>
                  {persona.name?.split(" ").pop()}
                </div>
              )}
              {personaNav.map((item) => (
                <button
                  key={item.id}
                  style={S.navItem(view === item.id)}
                  onClick={() => handleNav(item)}
                  title={collapsed ? item.label : undefined}
                >
                  <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon}</span>
                  {!collapsed && <span>{item.label}</span>}
                </button>
              ))}
            </>
          )}
        </nav>

        {/* Footer — logout */}
        <div style={{ borderTop: "1px solid #1e2d45", padding: "10px 14px" }}>
          <button
            onClick={onLogout}
            style={S.navItem(false)}
          >
            <span style={{ fontSize: 16 }}>🚪</span>
            {!collapsed && <span>Log out</span>}
          </button>
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {children}
      </div>
    </div>
  );
}
